// Share cards drawn on a canvas: 1080x1080 (feed / WhatsApp) and 1080x1920
// (WhatsApp Status / Instagram Story). Brand frame on every card (Bhaskar's
// standard): Kathakala logo top-left, BPlusWithBhaskar top-right, B+ circle
// bottom-left, Kathakala emblem bottom-right. On the tall card all key text
// stays inside Instagram's safe zone (campaign/safe_zone.py): x 60-900,
// y 250-1300; below that only decoration. No contact details ever go on a card.

import { forestLabel } from "./forests";

export const SITE = "https://sritaru.kathakala.us";
export const SITE_SHORT = "sritaru.kathakala.us";
export const PRICE_LINE = "$1.80 or ₹150 a tree";

export const FORMATS = {
  square: { w: 1080, h: 1080, label: "Square (feed, WhatsApp)" },
  story: { w: 1080, h: 1920, label: "Status / Story" },
};

// Telugu inside English lines (dedications) falls back to the phone's own
// Telugu font; Sree Krushnadevaraya is only for the campaign name.
const SANS = "'Helvetica Neue', Helvetica, Arial, 'Kohinoor Telugu', 'Telugu Sangam MN', 'Noto Sans Telugu', sans-serif";
const TELUGU = "'Sree Krushnadevaraya', serif";
const GOLD = "#f5d27a";
const WHITE = "#ffffff";
const MINT = "#bbf7d0";

const _images = {};
function loadImage(src) {
  if (!_images[src]) {
    _images[src] = new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });
  }
  return _images[src];
}

async function brandImages() {
  const [tl, tr, bl, br] = await Promise.all(
    ["kathakala_logo.png", "bplus_withbhaskar.jpg", "bplus_circular.png", "kathakala_emblem_circular.png"]
      .map((f) => loadImage(`/brand/${f}`))
  );
  return { tl, tr, bl, br };
}

const drawH = (ctx, img, x, y, h) => {
  const w = (img.width * h) / img.height;
  ctx.drawImage(img, x, y, w, h);
  return w;
};

function drawBrand(ctx, W, H, b) {
  drawH(ctx, b.tl, 30, 24, 130);
  const trW = (b.tr.width * 84) / b.tr.height;
  ctx.drawImage(b.tr, W - trW - 36, 46, trW, 84);
  drawH(ctx, b.bl, 36, H - 156, 120);
  drawH(ctx, b.br, W - 156, H - 156, 120);
}

function background(ctx, W, H) {
  const g = ctx.createLinearGradient(0, 0, 0, H);
  g.addColorStop(0, "#08461f");
  g.addColorStop(0.6, "#14652f");
  g.addColorStop(1, "#1c7a3a");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);
  // soft light behind the text
  const r = ctx.createRadialGradient(W / 2, H * 0.4, 50, W / 2, H * 0.4, W * 0.8);
  r.addColorStop(0, "rgba(255,255,255,0.10)");
  r.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = r;
  ctx.fillRect(0, 0, W, H);
}

// A low hill with a row of young trees: decoration for the covered bottom of the tall card.
function drawGrove(ctx, W, top, bottom) {
  ctx.fillStyle = "rgba(10, 60, 28, 0.85)";
  ctx.beginPath();
  ctx.moveTo(0, bottom);
  ctx.lineTo(0, top + 120);
  ctx.quadraticCurveTo(W / 2, top - 40, W, top + 120);
  ctx.lineTo(W, bottom);
  ctx.closePath();
  ctx.fill();
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  const trees = ["🌳", "🌱", "🌳", "🌿", "🌳", "🌱", "🌳"];
  trees.forEach((t, i) => {
    const x = 90 + (i * (W - 180)) / (trees.length - 1);
    const lift = Math.sin((i / (trees.length - 1)) * Math.PI) * 70;
    ctx.font = `${i % 2 ? 70 : 110}px ${SANS}`;
    ctx.fillText(t, x, top + 150 - lift);
  });
}

// Wrap, then narrow the width as far as the line count allows, so lines come
// out even ("in memory of my grandmother / Smt. Kamala", not a lone last word).
function wrapBalanced(ctx, text, maxW) {
  const lines = wrap(ctx, text, maxW);
  if (lines.length < 2) return lines;
  let lo = maxW / 2, hi = maxW;
  while (hi - lo > 4) {
    const mid = (lo + hi) / 2;
    if (wrap(ctx, text, mid).length > lines.length) lo = mid;
    else hi = mid;
  }
  return wrap(ctx, text, hi);
}

function wrap(ctx, text, maxW) {
  const words = String(text).split(/\s+/).filter(Boolean);
  const lines = [];
  let line = "";
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width <= maxW || !line) line = test;
    else {
      lines.push(line);
      line = w;
    }
  }
  if (line) lines.push(line);
  return lines;
}

// Lay out blocks top-down; long blocks shrink, then cut to maxLines with "…".
function layout(ctx, blocks, blocksMaxW) {
  return blocks.map((b) => {
    const maxW = b.pill ? blocksMaxW - 60 : blocksMaxW; // room for the pill's padding
    let size = b.size;
    const font = () => `${b.weight || "bold"} ${size}px ${b.family || SANS}`;
    ctx.font = font();
    let lines = wrapBalanced(ctx, b.text, maxW);
    const maxLines = b.maxLines || 2;
    // A single long word (a link) can't wrap, so it shrinks instead.
    const tooWide = () => lines.some((l) => ctx.measureText(l).width > maxW);
    while ((lines.length > maxLines || tooWide()) && size > b.size * (b.pill ? 0.5 : 0.7)) {
      size -= 2;
      ctx.font = font();
      lines = wrapBalanced(ctx, b.text, maxW);
    }
    if (lines.length > maxLines) {
      lines = lines.slice(0, maxLines);
      let last = lines[maxLines - 1];
      while (ctx.measureText(`${last}…`).width > maxW && last.length > 1) last = last.slice(0, -1);
      lines[maxLines - 1] = `${last.trimEnd()}…`;
    }
    const lineH = Math.round(size * (b.lineHeight || 1.18));
    return { ...b, size, font: font(), lines, lineH, height: lines.length * lineH };
  });
}

// Scale the big lines (not the small print) from `start` down until the blocks fit the height.
function fit(ctx, blocks, maxW, room, start = 1) {
  const scaled = (k) =>
    blocks.map((b) => (b.size < 48 ? b : { ...b, size: Math.floor(b.size * k), gap: Math.floor((b.gap ?? 20) * k) }));
  let laid = layout(ctx, scaled(start), maxW);
  for (let k = start - 0.03; blocksHeight(laid) > room && k > 0.5; k -= 0.03) {
    laid = layout(ctx, scaled(k), maxW);
  }
  return laid;
}

function blocksHeight(laid) {
  return laid.reduce((h, b, i) => h + (i ? b.gap ?? 20 : 0) + b.height, 0);
}

function drawBlocks(ctx, laid, x, y, align) {
  ctx.textAlign = align;
  ctx.textBaseline = "top";
  laid.forEach((b, i) => {
    if (i) y += b.gap ?? 20;
    ctx.font = b.font;
    b.lines.forEach((line) => {
      if (b.pill) {
        const w = ctx.measureText(line).width + 56;
        const left = align === "center" ? x - w / 2 : x - 4;
        ctx.fillStyle = GOLD;
        roundRect(ctx, left, y - 14, w, b.lineH + 16, (b.lineH + 16) / 2);
        ctx.fill();
        ctx.fillStyle = "#08461f";
        ctx.fillText(line, align === "center" ? x : x + 24, y);
      } else {
        ctx.fillStyle = b.color || WHITE;
        ctx.fillText(line, x, y);
      }
      y += b.lineH;
    });
  });
  return y;
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

// "in memory of Amma" stays as is; "Amma" becomes "for Amma"; Telugu stays as typed.
export function dedicationPhrase(d) {
  const t = (d || "").trim();
  if (!t) return "";
  if (!/^[A-Za-z]/.test(t)) return t;
  return /^(in\s+(loving\s+)?(memory|hono(u)?r)|for\b|to\b|dedicated\b)/i.test(t) ? t : `for ${t}`;
}

export const treesWord = (n) => `tree${n === 1 ? "" : "s"}`;

// The campaign name; on the square card it sits in the header between the logos.
const TITLE = { text: "శ్రీతరు సంకల్పం", size: 64, family: TELUGU, weight: "normal", color: GOLD, maxLines: 1, title: true };

// Blocks for a donor's card.
export function donorBlocks({ trees, dedication, pledged, forestName }) {
  const n = Number(trees) || 0;
  const blocks = [
    TITLE,
    { text: pledged ? "I pledged" : "I planted", size: 64, gap: 26 },
    { text: n.toLocaleString("en-US"), size: n >= 10000 ? 190 : 240, color: GOLD, maxLines: 1, gap: 0, lineHeight: 1.0 },
    { text: `fruit ${treesWord(n)} 🌱`, size: 72, gap: 6 },
  ];
  const phrase = dedicationPhrase(dedication);
  if (phrase) blocks.push({ text: phrase, size: 52, weight: "600", color: MINT, maxLines: 3, gap: 26 });
  if (forestName) blocks.push({ text: `in the ${forestLabel(forestName)}`, size: 40, weight: "500", color: MINT, maxLines: 2, gap: 14 });
  return blocks;
}

// Blocks for a group forest's card; a new forest with no trees yet invites the first one.
export function forestBlocks({ forestName, trees, donors }) {
  const n = Number(trees) || 0;
  if (n === 0)
    return [
      TITLE,
      { text: forestLabel(forestName), size: 76, maxLines: 2, gap: 26 },
      { text: "is open 🌱", size: 60, weight: "500", color: MINT, gap: 14 },
      { text: "Plant the first fruit tree", size: 84, color: GOLD, maxLines: 2, gap: 40 },
    ];
  return [
    TITLE,
    { text: forestLabel(forestName), size: 68, maxLines: 2, gap: 26 },
    { text: "has planted", size: 50, weight: "500", color: MINT, gap: 8 },
    { text: n.toLocaleString("en-US"), size: n >= 10000 ? 190 : 240, color: GOLD, maxLines: 1, gap: 6, lineHeight: 1.0 },
    { text: `fruit ${treesWord(n)} 🌳`, size: 72, gap: 6 },
    ...(donors ? [{ text: `from ${donors} ${donors === 1 ? "person" : "people"} so far`, size: 40, weight: "500", color: MINT, gap: 14 }] : []),
  ];
}

const callToAction = (lead, url) => [
  { text: lead, size: 60, gap: 40 },
  { text: url, size: 50, pill: true, maxLines: 1, gap: 26 },
  { text: `${PRICE_LINE} · on farmers' land in AP & Telangana`, size: 34, weight: "500", color: MINT, maxLines: 2, gap: 30 },
];

// ctaUrl: the link printed on the card, e.g. "sritaru.kathakala.us/forest/recw".
export async function renderCard(canvas, format, blocks, ctaLead = "Your turn 🌱", ctaUrl = SITE_SHORT) {
  const { w: W, h: H } = FORMATS[format];
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  try {
    await document.fonts.load(`64px 'Sree Krushnadevaraya'`, "శ్రీతరు");
  } catch (e) {
    /* falls back to a system Telugu font */
  }
  const brand = await brandImages();
  background(ctx, W, H);
  const all = [...blocks, ...callToAction(ctaLead, ctaUrl)];

  if (format === "story") {
    // Safe zone: x 60-900, y 250-1300. Left-aligned at x 80, 800 wide.
    drawGrove(ctx, W, 1460, H);
    const laid = fit(ctx, all, 800, 1290 - 290, 1.2);
    drawBlocks(ctx, laid, 80, 290 + Math.max(0, (1000 - blocksHeight(laid)) / 3), "left");
  } else {
    const title = all.find((b) => b.title);
    if (title) drawBlocks(ctx, layout(ctx, [title], 520), W / 2, 62, "center");
    const laid = fit(ctx, all.filter((b) => !b.title), 880, 930 - 190);
    drawBlocks(ctx, laid, W / 2, 190 + (740 - blocksHeight(laid)) / 2, "center");
  }
  drawBrand(ctx, W, H, brand);
  return canvas;
}

export const canvasToBlob = (canvas) =>
  new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
